import { Controller } from "@hotwired/stimulus"
import { patch, post } from "@rails/request.js"
import { phoneDigits } from "../lib/phone_digits"
import { syncGateReason } from "../lib/gate_reason"
import { setButtonLoading } from "../lib/button_loading"
import { PURCHASED_EVENT } from "./phone_number_purchase_controller"
import { retoneStatusBadge } from "../lib/status_badge"

// Trust Center Wizard
//
// Client-side panel wizard with a server round-trip per step. Each "Continue"
// PATCHes the step over @rails/request.js to TrustCenterStepService. On success
// the server advances the resume pointer and returns the updated status badge;
// on a 422 it returns { errors: [...], field_errors: { field: [msg] } }, which
// we surface inline (.is-invalid + .invalid-feedback) and in a summary alert.
//
// Ready Gate (DESIGN.md PATTERN-3, #18683 — owner 2026-10-22: "buttons should
// be disabled until all fields are filled out and validated"). Each step's
// Continue lives in its last card's Card Footer (PATTERN-2) and stays disabled
// until every visible required field on the step is filled AND passes the
// client checks below, which mirror TrustCenterProfile / TrustCenterStepService
// (the server still re-validates). The footer's hint names what's left
// ("2 left: Personal Email · Verify Your Cell") and the disabled button carries
// the same reason as its tooltip and accessible name (INTERACTION-5 / BUTTON-4).
// Step 2's address blocks also need the USPS Check (PATTERN-16): a debounced
// verify_address call (TrustCenter::AddressCheck → SmartyStreets) whose
// Keep As Entered / Use USPS Format box is the shared partial's markup. An
// address USPS can't find keeps the gate closed with what to fix; a USPS
// outage never blocks.
//
// Five steps (#18737): Business Type · Your Information · Texting Setup ·
// Review & Submit · Carrier Approval. Continue saves the first three. Texting
// Setup's gate is the GOAT Connect number (the numberStatus target carries the
// server's readiness; a purchase on this page — PURCHASED_EVENT — opens it).
// Review & Submit has no Continue: its own controller
// (trust_center_review_submit_controller) gates and sends Submit to Carriers,
// then fires `trust-center-review-submit:submitted`, which lands here
// (#carriersSubmitted) to save the progress and reload onto Carrier Approval.
//
// Step 1 (brand type) drives which Step 2 field group is shown — selecting a
// brand toggles the matching .trust-center-brand-fields group; only the visible
// group is gated and submitted, so stale fields from another brand path never
// cross the wire.
//
// The CRM setup tour (#16204) and the shared unsaved-changes controller read
// `data-trust-center-wizard-dirty` ("true" while any panel holds an edit its
// step has not saved — Continue saves only the panel on screen, so each panel
// is tracked on its own). A bubbling TOUR_COMMIT_EVENT fires once a Continue
// has saved (never on a click that validation stopped).
export const TOUR_COMMIT_EVENT = "gl-tour:commit"

const EMAIL_FORMAT = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const POSTAL_CODE_FORMATS = {
  US: /^\d{5}(-\d{4})?$/,
  CA: /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/,
}
const ADDRESS_CHECK_DELAY_MS = 600
const INFORMATION_STEP = 1
const TEXTING_SETUP_STEP = 2
const REVIEW_SUBMIT_STEP = 3
const CARRIER_APPROVAL_STEP = 4
const NUMBER_LABEL = "GOAT Connect Number"
const VERIFY_CELL_LABEL = "Verify Your Cell"
// Where USPS's suggested parts land (AddressCorrectionDetection's keys).
const CORRECTION_ROLES = { street: "street", secondary: "secondary", city: "city", state: "region", zip: "postal_code" }

export default class extends Controller {
  static targets = [
    "panel", "step", "statusBadge",
    "errorAlert", "errorList", "errorTitle", "errorIcon",
    "brandCard", "brandFields", "brandRecommendation",
    "einBrandRadio", "backButton", "nextButton", "readyHint",
    "addressBlock", "addressResult",
    "numberStatus", "numberPurchased", "buyNumber",
  ]

  // Summary-alert copy per tone. The box is shared, so heading and icon MUST
  // move with the tone: a blue box still headlined "We couldn't save this
  // step" is the exact confusing message this wizard is trying to eliminate.
  static SUMMARY_TONES = {
    danger: { title: "We couldn't save this step", icon: "ph-warning-circle" },
    info: { title: "Heads up", icon: "ph-info" },
  }

  static values = {
    currentStep: Number,
    // Steps the agent has saved with Continue (server's steps_completed). Only
    // these turn green: a step is never "done" just because it sits before the
    // one on screen (PATTERN-4).
    completedSteps: Array,
    totalSteps: Number,
    updateUrl: String,
    verifyAddressUrl: String,
    // TrustCenterProfile::CONSUMER_EMAIL_DOMAINS / DISTRIBUTION_EMAIL_LOCAL_PARTS.
    consumerDomains: Array,
    distributionLocalParts: Array,
  }

  connect() {
    this.dirtyPanels = new Set()
    this.addressState = {}
    this.addressTimers = {}
    this.busy = false
    this.onEdit = (event) => {
      // Not a step field (Review & Submit's "I agree" box): nothing to save, never "unsaved".
      if (event.target instanceof Element && event.target.closest("[data-wizard-no-dirty]")) return
      this.markPanelDirty(this.panelIndexFor(event.target))
      if (event.type === "change") this.syncRegionOptions(event.target)
      this.scheduleAddressCheck(event.target)
      this.refreshGate()
    }
    this.onFieldLeft = (event) => this.flagInvalidField(event.target)
    this.onUspsChoice = (event) => this.applyUspsChoice(event)
    this.onOtpVerified = () => this.refreshGate()
    this.onNumberPurchased = () => this.numberPurchased()
    document.addEventListener(PURCHASED_EVENT, this.onNumberPurchased)
    this.element.addEventListener("input", this.onEdit)
    this.element.addEventListener("change", this.onEdit)
    this.element.addEventListener("focusout", this.onFieldLeft)
    this.element.addEventListener("click", this.onUspsChoice)
    this.element.addEventListener("trust-center-otp:verified", this.onOtpVerified)
    this.syncDirty()
    // The server-provided current step is the furthest the user has reached;
    // it gates forward jumps via the stepper while back-navigation stays free.
    this.furthestStep = this.currentStepValue
    this.syncRegionOptions()
    this.updateBrandRecommendation()
    this.updateBrandFields()
    this.showStep(this.currentStepValue)
  }

  disconnect() {
    this.element.removeEventListener("input", this.onEdit)
    this.element.removeEventListener("change", this.onEdit)
    this.element.removeEventListener("focusout", this.onFieldLeft)
    this.element.removeEventListener("click", this.onUspsChoice)
    this.element.removeEventListener("trust-center-otp:verified", this.onOtpVerified)
    document.removeEventListener(PURCHASED_EVENT, this.onNumberPurchased)
    Object.values(this.addressTimers).forEach((timer) => clearTimeout(timer))
  }

  // The panel an edit belongs to; one outside every panel, to the step on screen.
  panelIndexFor(element) {
    const panel = element instanceof Element ? element.closest("[data-trust-center-wizard-target='panel']") : null
    const index = panel ? this.panelTargets.indexOf(panel) : -1
    return index >= 0 ? index : this.currentStepValue
  }

  markPanelDirty(index) {
    this.dirtyPanels.add(index)
    this.syncDirty()
  }

  syncDirty() {
    this.element.dataset.trustCenterWizardDirty = String(this.dirtyPanels.size > 0)
  }

  // Nothing is left unsaved — the server took it (Submit to Carriers, or a
  // Carrier Approval action that reloads the page, #19798).
  clearDirty() {
    this.dirtyPanels.clear()
    this.syncDirty()
  }

  // ----- Navigation -------------------------------------------------------

  goToStep(event) {
    const target = parseInt(event.currentTarget.dataset.step, 10)
    if (Number.isNaN(target)) return
    // Free to revisit any step already reached; Continue is the only way forward.
    if (target <= this.furthestStep) this.showStep(target)
  }

  prev(event) {
    event.preventDefault()
    if (this.currentStepValue > 0) this.showStep(this.currentStepValue - 1)
  }

  async next(event) {
    event.preventDefault()
    if (this.busy) return
    // The button is disabled while the gate is closed; a programmatic click
    // still gets the inline marks rather than a silent no-op.
    if (this.readinessItems(this.currentStepValue).length > 0) {
      this.validateActivePanel()
      return
    }

    const savedStep = this.currentStepValue
    this.setBusy(true)
    try {
      const response = await patch(this.updateUrlValue, {
        body: this.collectStepData(),
        responseKind: "json",
      })
      const data = await response.json
      if (response.ok && data.ok) {
        this.applyServerState(data)
        this.markStepCompleted(savedStep)
        this.dirtyPanels.delete(savedStep)
        this.syncDirty()
        this.element.dispatchEvent(new CustomEvent(TOUR_COMMIT_EVENT, { bubbles: true }))
        this.showStep(Math.min(this.currentStepValue + 1, this.totalStepsValue - 1))
      } else if (response.ok && data.guarded) {
        // Continue tried to submit the campaign and hit an environment-level
        // guard (module off, missing brand binding). Informational — same
        // neutral tone the campaign panel's own Submit button uses — never a
        // red validation failure. The wizard stays on this step.
        this.applyServerState(data)
        this.showSummary([data.message || "Not available in this environment yet."], "info")
      } else {
        this.handleErrors(data || {})
      }
    } catch {
      this.showSummary(["Something went wrong saving this step. Please try again."])
    } finally {
      this.setBusy(false)
    }
  }

  // ----- Texting Setup: the GOAT Connect number ---------------------------

  numberReady() {
    if (!this.hasNumberStatusTarget) return true
    return this.numberStatusTarget.dataset.ready === "true"
  }

  // A number bought on this page (the one Purchase Phone Number dialog,
  // #18494). The server re-checks readiness when Continue saves the step.
  numberPurchased() {
    if (!this.hasNumberStatusTarget || this.numberReady()) return
    this.numberStatusTarget.dataset.ready = "true"
    if (this.hasNumberPurchasedTarget) this.numberPurchasedTarget.classList.remove("d-none")
    if (this.hasBuyNumberTarget) this.buyNumberTarget.hidden = true
    this.refreshGate()
  }

  // ----- Review & Submit -> Carrier Approval ------------------------------

  // Submit to Carriers succeeded (trust_center_review_submit_controller). The
  // server saved steps 1-4 and moved the pointer; Carrier Approval renders
  // from the brand and campaign it just created, so the page reloads onto it.
  carriersSubmitted(event) {
    const data = event?.detail || {}
    this.applyServerState(data)
    for (let step = 0; step <= REVIEW_SUBMIT_STEP; step += 1) this.markStepCompleted(step)
    this.clearDirty()
    this.element.dispatchEvent(new CustomEvent(TOUR_COMMIT_EVENT, { bubbles: true }))
    this.furthestStep = CARRIER_APPROVAL_STEP
    this.showStep(CARRIER_APPROVAL_STEP)
    this.reloadPage()
  }

  reloadPage() {
    window.location.reload()
  }

  // ----- Brand selection (Step 1 -> Step 2 field set) ---------------------

  selectBrand() {
    this.brandCardTargets.forEach((card) => {
      const input = card.querySelector('input[type="radio"]')
      card.classList.toggle("selected", input?.checked)
    })
    this.updateBrandRecommendation()
    this.updateBrandFields()
    this.refreshGate()
  }

  // The EIN answer defaults to low_volume_standard; full standard is an
  // explicit opt-in from the recommendation panel, never a peer answer. The
  // override swaps the EIN radio's VALUE (never adds a third radio) so the
  // answer group stays two named choices for keyboard/screen-reader users —
  // and a plain card re-click re-activates the same radio with its current
  // value, so an existing standard choice can never silently downgrade.
  // One handler for both panel links; the link's data-brand carries the value.
  // Activating a link hides the panel that contains it, which would drop
  // keyboard focus to <body> — so focus moves to the newly shown panel's
  // title (tabindex="-1") and Tab continues from the decision the user just
  // made instead of the top of the page.
  chooseBrand(event) {
    event.preventDefault()
    this.setBrand(event.currentTarget.dataset.brand)
    // Set in code, so no input/change event marks it: an unsaved answer all the same.
    this.markPanelDirty(this.panelIndexFor(event.currentTarget))
    const shown = this.brandRecommendationTargets.find((el) => !el.hidden)
    shown?.querySelector(".gl-alert-title")?.focus()
  }

  setBrand(value) {
    if (value === "sole_proprietor") {
      const radio = this.element.querySelector('input[name="trust_center_profile[brand_type]"][value="sole_proprietor"]')
      if (!radio) return
      radio.checked = true
    } else {
      if (!this.hasEinBrandRadioTarget) return
      this.einBrandRadioTarget.value = value
      this.einBrandRadioTarget.checked = true
    }
    this.selectBrand()
  }

  updateBrandRecommendation() {
    const checked = this.element.querySelector('input[name="trust_center_profile[brand_type]"]:checked')
    this.brandRecommendationTargets.forEach((el) => {
      el.hidden = el.dataset.brand !== checked?.value
    })
  }

  updateBrandFields() {
    const group = this.selectedBrandGroup()
    this.brandFieldsTargets.forEach((el) => {
      el.hidden = el.dataset.brand !== group
    })
    this.updateConditionalFields()
  }

  updateConditionalFields() {
    const companyType = this.element.querySelector("#tc_company_type")?.value
    this.element.querySelectorAll("[data-provider-condition]").forEach((section) => {
      const visible = section.dataset.providerCondition === "public-company" && companyType === "public"
      section.hidden = !visible
    })
    this.refreshGate()
  }

  // 'sole_proprietor' has its own field set; both EIN brand types share 'ein'.
  selectedBrandGroup() {
    const checked = this.element.querySelector('input[name="trust_center_profile[brand_type]"]:checked')
    if (!checked) return null
    return checked.value === "sole_proprietor" ? "sole_proprietor" : "ein"
  }

  // ----- State / Province select -----------------------------------------

  // Enable only the region group matching its block's country; a choice from
  // the other country's group is cleared (the server would refuse it).
  syncRegionOptions(changed = null) {
    this.element.querySelectorAll("select[data-gate-check='region']").forEach((select) => {
      const countryId = select.dataset.countryField
      if (changed && changed !== select && changed.id !== countryId) return
      const country = this.element.querySelector(`#${countryId}`)?.value || "US"
      select.querySelectorAll("optgroup[data-country]").forEach((group) => {
        const matches = group.dataset.country === country
        group.disabled = !matches
        group.hidden = !matches
      })
      const selected = select.selectedOptions?.[0]
      if (selected?.parentElement?.disabled) select.value = ""
    })
  }

  // ----- Ready Gate (PATTERN-3) -------------------------------------------

  // What still stands between the step and its Continue, as short labels.
  readinessItems(step) {
    if (step === 0) {
      const checked = this.element.querySelector('input[name="trust_center_profile[brand_type]"]:checked')
      return checked ? [] : ["Business Type"]
    }
    if (step === TEXTING_SETUP_STEP) return this.numberReady() ? [] : [NUMBER_LABEL]
    if (step !== INFORMATION_STEP) return [] // Review & Submit and Carrier Approval have no Continue.

    const group = this.visibleBrandFields()
    if (!group) return ["Business Type"]

    const items = []
    this.gatedFields(group).forEach((field) => {
      if (!field.value.trim() || this.fieldProblem(field)) items.push(this.labelFor(field))
    })
    if (this.cellNeedsVerifying(group)) items.push(VERIFY_CELL_LABEL)
    this.addressBlocksIn(group).forEach((block) => {
      const item = this.addressGateItem(block)
      if (item && !items.includes(item)) items.push(item)
    })
    return items
  }

  gatedFields(group) {
    return Array.from(group.querySelectorAll("input:not([type=radio]), select, textarea")).filter((field) => {
      // Inputs owned by a nested controller (e.g. the sole-prop OTP code field,
      // driven by trust-center-otp) and optional inputs opt out of the gate.
      if (field.hasAttribute("data-wizard-skip") || field.hasAttribute("data-wizard-optional")) return false
      return !field.closest("[hidden]")
    })
  }

  labelFor(field) {
    return field.dataset.gateLabel || field.labels?.[0]?.textContent?.trim() || field.name
  }

  // The format problem with a filled field, or null. Mirrors TrustCenterProfile
  // validations and TrustCenterStepService#check_postal_codes — keep in step.
  fieldProblem(field) {
    const value = field.value.trim()
    if (!value) return null
    switch (field.dataset.gateCheck) {
      case "email":
        return EMAIL_FORMAT.test(value) ? null : "Enter a full email address, like name@company.com."
      case "consumer_email":
        if (!EMAIL_FORMAT.test(value)) return "Enter a full email address, like name@gmail.com."
        return this.consumerDomain(value)
          ? null
          : "Use a personal email (Gmail, Yahoo, Outlook, iCloud…), not a work address."
      case "brand_contact_email":
        return this.brandContactProblem(value, field)
      case "phone":
        return phoneDigits(value).length === 10 ? null : "Enter a 10-digit US or Canada number, like (555) 123-4567."
      case "ein":
        return this.einProblem(value, field)
      case "postal_code":
        return this.postalCodeProblem(field, value)
      case "region":
        return this.regionProblem(field)
      default:
        return null
    }
  }

  consumerDomain(email) {
    return this.consumerDomainsValue.includes(this.domainOf(email))
  }

  domainOf(email) {
    return email.trim().toLowerCase().split("@").pop().replace(/^www\./, "")
  }

  // The value of another profile field beside `field`: on Carrier Approval's
  // fix card its open twin (tc_fix_<attr>) when the carriers flagged it too,
  // else the saved Your Information field (#19798).
  siblingValue(field, attr) {
    const ids = field?.id?.startsWith("tc_fix_") ? [`tc_fix_${attr}`, `tc_${attr}`] : [`tc_${attr}`]
    const sibling = ids.map((id) => this.element.querySelector(`#${id}`)).find(Boolean)
    return sibling?.value?.trim() || ""
  }

  brandContactProblem(value, field = null) {
    if (!EMAIL_FORMAT.test(value)) return "Enter a full email address, like jane@yourcompany.com."
    if (this.consumerDomain(value)) return "Use your company email, not a personal mailbox."
    const repEmail = this.siblingValue(field, "auth_rep_email")
    if (!EMAIL_FORMAT.test(repEmail) || this.domainOf(repEmail) !== this.domainOf(value)) {
      const repDomain = EMAIL_FORMAT.test(repEmail) ? ` (@${this.domainOf(repEmail)})` : ""
      return `Use the same company domain as the representative's email${repDomain}.`
    }
    const local = value.trim().toLowerCase().split("@")[0].split("+")[0]
    const roles = this.distributionLocalPartsValue
    if (roles.includes(local.replace(/[^a-z0-9]/g, "")) || roles.includes(local.split(/[._-]/)[0])) {
      return "Use one person's address, not a shared one like info@ or support@."
    }
    return null
  }

  einProblem(value, field = null) {
    const country = this.siblingValue(field, "business_iso_country") || "US"
    if (country === "CA") {
      return /^\d{9}$/.test(value.replace(/[\s-]/g, "")) ? null : "Enter your 9-digit Business Number, like 123456789."
    }
    return /^\d{2}-?\d{7}$/.test(value) ? null : "Enter your 9-digit EIN, like 12-3456789."
  }

  postalCodeProblem(field, value) {
    const country = this.addressValue(field.closest("[data-trust-center-wizard-target='addressBlock']"), "country") || "US"
    const format = POSTAL_CODE_FORMATS[country]
    if (!format || format.test(value)) return null
    return country === "CA" ? "Enter a postal code like K1A 0B1." : "Enter a 5-digit ZIP like 75201."
  }

  regionProblem(select) {
    const country = this.element.querySelector(`#${select.dataset.countryField}`)?.value || "US"
    const group = select.selectedOptions?.[0]?.parentElement
    if (group?.dataset?.country === country) return null
    return country === "CA" ? "Choose your province from the list." : "Choose your state from the list."
  }

  // The sole proprietor's cell counts as verified only while the field still
  // holds the number the server verified (trust-center-otp keeps it).
  cellNeedsVerifying(group) {
    const otp = group.querySelector("[data-controller~='trust-center-otp']")
    const phone = group.querySelector("#tc_mobile_phone")
    if (!otp || !phone || this.fieldProblem(phone) || !phone.value.trim()) return false
    const verified = otp.dataset.verifiedPhone || ""
    return verified === "" || phoneDigits(verified) !== phoneDigits(phone.value)
  }

  refreshGate() {
    const step = this.currentStepValue
    const items = this.readinessItems(step)
    const reason = items.length ? `${items.length} left: ${items.join(" · ")}` : ""
    const panel = this.panelTargets[step]

    this.readyHintTargets.forEach((hint) => {
      if (panel?.contains(hint)) hint.textContent = reason
    })
    // The shared gate writer (lib/gate_reason): disabled + the reason as the
    // tooltip and "Continue — <reason>" name; never re-opens a busy button.
    this.nextButtonTargets.forEach((button) => {
      if (panel?.contains(button)) syncGateReason(button, { ready: items.length === 0, reason })
    })
    return items
  }

  // An inline mark once the agent leaves a filled field that fails its check.
  flagInvalidField(field) {
    if (!(field instanceof Element) || !field.dataset?.gateCheck) return
    const problem = this.fieldProblem(field)
    if (problem) this.markInvalid(field, problem)
  }

  // ----- USPS Check (PATTERN-16) -----------------------------------------

  addressBlocksIn(group) {
    return this.addressBlockTargets.filter((block) => group.contains(block) && !block.closest("[hidden]"))
  }

  addressFields(block) {
    try {
      return JSON.parse(block?.dataset?.addressFields || "{}")
    } catch {
      return {}
    }
  }

  addressInput(block, role) {
    const id = this.addressFields(block)[role]
    return id ? this.element.querySelector(`#${id}`) : null
  }

  addressValue(block, role) {
    return this.addressInput(block, role)?.value?.trim() || ""
  }

  address(block) {
    const roles = ["street", "secondary", "city", "region", "postal_code", "country"]
    return Object.fromEntries(roles.map((role) => [role, this.addressValue(block, role)]))
  }

  addressKey(address) {
    return JSON.stringify(address).toLowerCase()
  }

  // Checkable = a US address whose parts are filled and pass their own checks.
  addressCheckable(block) {
    const address = this.address(block)
    if (address.country !== "US") return false
    const required = ["street", "city", "region", "postal_code"]
    if (required.some((role) => !address[role])) return false
    return !["region", "postal_code"].some((role) => this.fieldProblem(this.addressInput(block, role)))
  }

  addressGateItem(block) {
    if (!this.addressCheckable(block)) return null
    const state = this.addressState[block.dataset.block]
    const current = state && state.key === this.addressKey(this.address(block))
    if (!current || state.status === "pending") return "USPS Address Check"
    if (state.status === "refused") return "Fix the Address USPS Can't Find"
    return null
  }

  scheduleAddressCheck(field) {
    const block = field instanceof Element ? field.closest("[data-trust-center-wizard-target='addressBlock']") : null
    if (!block || !this.addressBlockTargets.includes(block)) return
    clearTimeout(this.addressTimers[block.dataset.block])
    this.addressTimers[block.dataset.block] = setTimeout(() => this.checkAddress(block), ADDRESS_CHECK_DELAY_MS)
  }

  // Check every visible block that is ready and not yet checked as it stands.
  checkVisibleAddresses() {
    const group = this.visibleBrandFields()
    if (!group) return
    this.addressBlocksIn(group).forEach((block) => {
      const state = this.addressState[block.dataset.block]
      if (!state || state.key !== this.addressKey(this.address(block))) this.checkAddress(block)
    })
  }

  async checkAddress(block) {
    const name = block.dataset.block
    if (!this.addressCheckable(block)) {
      delete this.addressState[name]
      this.renderAddressResult(block, null)
      this.refreshGate()
      this.announceAddress(block)
      return
    }

    const address = this.address(block)
    const key = this.addressKey(address)
    if (this.addressState[name]?.key === key && this.addressState[name].status !== "pending") return
    this.addressState[name] = { key, status: "pending" }
    this.renderAddressResult(block, { status: "pending" })
    this.refreshGate()

    let state
    try {
      const response = await post(this.verifyAddressUrlValue, {
        body: JSON.stringify(address),
        contentType: "application/json",
        responseKind: "json",
      })
      const data = (await response.json) || {}
      state = { key, status: data.status || "unavailable", message: data.message, corrections: data.corrections, html: data.html }
    } catch {
      // Never block on our own outage — the step save re-checks server-side.
      state = { key, status: "unavailable", message: "We couldn't check this address with USPS right now. You can continue." }
    }
    // A newer edit superseded this answer.
    if (this.addressState[name]?.key !== key) return
    this.addressState[name] = state
    this.renderAddressResult(block, state)
    this.refreshGate()
    this.announceAddress(block)
  }

  // Tells a gate outside this controller that a block's USPS answer moved —
  // Carrier Approval's Send My Fix (#18783) runs the same check on its block.
  announceAddress(block) {
    this.dispatch("addressChecked", { target: block, detail: { block: block.dataset.block } })
  }

  renderAddressResult(block, state) {
    const result = this.addressResultTargets.find((el) => block.contains(el))
    if (!result) return
    result.replaceChildren()
    if (!state || state.status === "verified" || state.status === "skipped") {
      if (state?.status === "verified") result.append(this.addressNote("ph-check-circle", "USPS confirmed this address.", "success"))
      return
    }
    if (state.status === "pending") {
      result.append(this.addressNote("ph-spinner", "Checking this address with USPS…", "muted"))
    } else if (state.status === "corrections" && state.html) {
      // The shared USPS Check partial, rendered by the server (one markup).
      const template = document.createElement("template")
      template.innerHTML = state.html
      result.append(template.content)
    } else if (state.status === "refused") {
      result.append(this.addressNote("ph-warning-circle", `USPS couldn't find this address. ${state.message || ""}`.trim(), "danger"))
    } else if (state.status === "unavailable" && state.message) {
      result.append(this.addressNote("ph-info", state.message, "muted"))
    }
  }

  addressNote(icon, text, tone) {
    const note = document.createElement("p")
    // Pass/fail text is the shared contrast-safe tone (.gl-text-*); muted stays the hint's own grey.
    const toneClass = { success: "gl-text-success", danger: "gl-text-danger" }[tone] || ""
    note.className = `gl-form-hint trust-center-address-note ${toneClass}`.trim()
    const glyph = document.createElement("i")
    glyph.className = `ph ${icon} me-1`
    glyph.setAttribute("aria-hidden", "true")
    note.append(glyph, text)
    return note
  }

  // Keep As Entered / Use USPS Format inside a block's corrections box.
  applyUspsChoice(event) {
    const button = event.target instanceof Element ? event.target.closest("[data-usps-choice]") : null
    if (!button) return
    const block = button.closest("[data-trust-center-wizard-target='addressBlock']")
    const state = block && this.addressState[block.dataset.block]
    if (!state) return
    event.preventDefault()

    if (button.dataset.uspsChoice === "use") {
      Object.entries(state.corrections || {}).forEach(([part, values]) => {
        const input = this.addressInput(block, CORRECTION_ROLES[part])
        if (!input || values?.suggested == null) return
        // The fix card's approved parts are locked, unnamed hidden inputs Send My
        // Fix never posts: a correction there would show an address the server
        // never receives (#19807). The save re-checks USPS on what it stores.
        if (input.type === "hidden" && !input.name) return
        input.value = values.suggested
        input.classList.remove("is-invalid")
      })
      this.syncRegionOptions()
      // The fix card's block is sent by Send My Fix, never by a Continue.
      if (!block.closest("[data-wizard-no-dirty]")) this.markPanelDirty(this.panelIndexFor(block))
    }
    // Both answers leave a USPS-accepted address; the step save re-checks it.
    this.addressState[block.dataset.block] = { key: this.addressKey(this.address(block)), status: "verified" }
    this.renderAddressResult(block, this.addressState[block.dataset.block])
    this.refreshGate()
    this.announceAddress(block)
  }

  // ----- Validation (marks after a refused click or a 422) ----------------

  validateActivePanel() {
    this.clearErrors()
    const items = this.readinessItems(this.currentStepValue)
    if (items.length === 0) return true

    const group = this.currentStepValue === INFORMATION_STEP ? this.visibleBrandFields() : null
    if (group) {
      this.gatedFields(group).forEach((field) => {
        if (!field.value.trim()) this.markInvalid(field, `Enter your ${this.labelFor(field)}.`)
        else if (this.fieldProblem(field)) this.markInvalid(field, this.fieldProblem(field))
      })
    }
    this.showSummary([`Still needed: ${items.join(" · ")}.`])
    return false
  }

  // Clear a field's error state as soon as the user edits it.
  clearFieldError(event) {
    const field = event.target
    field.classList.remove("is-invalid")
    const feedback = field.nextElementSibling
    if (feedback?.classList.contains("invalid-feedback")) feedback.remove()
  }

  // ----- Server payload + response ---------------------------------------

  collectStepData() {
    const body = new FormData()
    body.append("step", this.currentStepValue)
    const panel = this.panelTargets[this.currentStepValue]
    if (!panel) return body

    panel.querySelectorAll("input, select, textarea").forEach((field) => {
      if (!field.name) return
      // Skip inputs inside a hidden brand-field group so the non-selected
      // brand path's values never reach the server.
      if (field.closest("[hidden]")) return
      if (field.type === "radio" && !field.checked) return
      body.append(field.name, field.value)
    })
    return body
  }

  markStepCompleted(step) {
    if (!this.completedStepsValue.includes(step)) this.completedStepsValue = [...this.completedStepsValue, step]
  }

  applyServerState(data) {
    if (typeof data.current_step === "number") {
      this.furthestStep = Math.max(this.furthestStep, data.current_step)
    }
    if (data.status && this.hasStatusBadgeTarget) {
      this.statusBadgeTarget.textContent = data.status.label
      // Swap only the TONE modifier: the badge rides the dark page hero and
      // keeps `gl-status-badge--on-dark` (the dark-canvas palette, #12564).
      retoneStatusBadge(this.statusBadgeTarget, data.status.modifier.replace(/^gl-status-badge--/, ""))
    }
  }

  handleErrors(data) {
    this.clearErrors()
    const fieldErrors = data.field_errors || {}
    Object.keys(fieldErrors).forEach((field) => {
      const input =
        this.element.querySelector(`[name="trust_center_profile[${field}]"]`) ||
        this.element.querySelector(`#tc_${field}`)
      if (input) this.markInvalid(input, fieldErrors[field][0])
    })
    this.showSummary(data.errors && data.errors.length ? data.errors : ["Please fix the highlighted fields."])
  }

  // ----- Display ----------------------------------------------------------

  showStep(index) {
    this.currentStepValue = index

    this.panelTargets.forEach((panel, i) => panel.classList.toggle("active", i === index))
    // gl-stepper: a completed step's own connector turns green via --complete,
    // so the step classes are the only progress state to maintain.
    this.stepTargets.forEach((step, i) => {
      step.classList.toggle("gl-stepper-step--active", i === index)
      step.classList.toggle("gl-stepper-step--complete", i !== index && this.completedStepsValue.includes(i))
    })

    this.clearErrors()
    this.refreshGate()
    if (index === INFORMATION_STEP) this.checkVisibleAddresses()
    window.scrollTo({ top: 0, behavior: "smooth" })
  }

  // BUTTON-8: the working Continue keeps its label, shows the spinner.
  setBusy(busy) {
    this.busy = busy
    // Remember which Continue spun: a successful save moves to the next step
    // before the request settles, and that button is the one to restore.
    if (busy) {
      const panel = this.panelTargets[this.currentStepValue]
      this.busyButtons = this.nextButtonTargets.filter((button) => panel?.contains(button))
    }
    ;(this.busyButtons || []).forEach((button) => setButtonLoading(button, busy))
    if (!busy) this.busyButtons = []
    this.backButtonTargets.forEach((button) => {
      button.disabled = busy
    })
    this.refreshGate()
  }

  // ----- Error rendering helpers -----------------------------------------

  markInvalid(input, message) {
    input.classList.add("is-invalid")
    const next = input.nextElementSibling
    if (next?.classList.contains("invalid-feedback")) {
      next.textContent = message
    } else {
      const feedback = document.createElement("div")
      feedback.className = "invalid-feedback"
      feedback.textContent = message
      input.parentNode.insertBefore(feedback, input.nextSibling)
    }
  }

  clearErrors() {
    this.element.querySelectorAll(".is-invalid").forEach((el) => el.classList.remove("is-invalid"))
    this.element.querySelectorAll(".invalid-feedback").forEach((el) => el.remove())
    if (this.hasErrorAlertTarget) this.errorAlertTarget.classList.add("d-none")
    if (this.hasErrorListTarget) this.errorListTarget.replaceChildren()
  }

  // tone: "danger" (default — validation failures) or "info" (environment
  // guards). One alert element, retoned per message — class, heading, and
  // icon together — so the two never stack and never contradict each other.
  showSummary(messages, tone = "danger") {
    if (!this.hasErrorAlertTarget || !this.hasErrorListTarget) return
    const copy = this.constructor.SUMMARY_TONES[tone] || this.constructor.SUMMARY_TONES.danger
    this.errorListTarget.replaceChildren()
    messages.forEach((message) => {
      const li = document.createElement("li")
      li.textContent = message
      this.errorListTarget.appendChild(li)
    })
    this.errorAlertTarget.classList.toggle("gl-alert--danger", tone !== "info")
    this.errorAlertTarget.classList.toggle("gl-alert--info", tone === "info")
    if (this.hasErrorTitleTarget) this.errorTitleTarget.textContent = copy.title
    if (this.hasErrorIconTarget) {
      this.errorIconTarget.classList.remove("ph-warning-circle", "ph-info")
      this.errorIconTarget.classList.add(copy.icon)
    }
    this.errorAlertTarget.classList.remove("d-none")
    this.errorAlertTarget.scrollIntoView?.({ behavior: "smooth", block: "nearest" })
  }

  visibleBrandFields() {
    return this.brandFieldsTargets.find((el) => !el.hidden) || null
  }
}
